import { type InfiniteData, type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AttachmentDTO,
  BootstrapDTO,
  ChannelDTO,
  ConversationDTO,
  InviteDTO,
  InviteInfoDTO,
  MeDTO,
  MessageDTO,
  MessagePage,
  MoveRequestDTO,
  NotificationPage,
  NotifyLevel,
  ReactionDTO,
  ThreadDTO,
  ThreadSummaryDTO,
  UserDTO,
} from "@justone/shared";
import { http } from "./http";

/** 메시지가 사는 곳: 채널 또는 DM 대화 */
export interface RoomRef {
  kind: "channel" | "dm";
  id: number;
}

export const roomOfMessage = (m: Pick<MessageDTO, "channelId" | "conversationId">): RoomRef =>
  m.channelId !== null ? { kind: "channel", id: m.channelId } : { kind: "dm", id: m.conversationId! };

const roomApi = (r: RoomRef) => (r.kind === "channel" ? `/api/channels/${r.id}` : `/api/conversations/${r.id}`);

/** 화면 주소 */
export const roomPath = (r: RoomRef, rootId?: number | null) => `${r.kind === "channel" ? "/c" : "/d"}/${r.id}${rootId ? `/t/${rootId}` : ""}`;

export const keys = {
  bootstrap: ["bootstrap"] as const,
  channels: ["channels"] as const,
  conversations: ["conversations"] as const,
  users: ["users"] as const,
  messages: (r: RoomRef) => ["messages", r.kind, r.id] as const,
  invites: ["invites"] as const,
  thread: (rootId: number) => ["thread", rootId] as const,
  notifications: ["notifications"] as const,
  threads: ["threads"] as const,
  moves: (conversationId: number) => ["moves", conversationId] as const,
  invite: (token: string) => ["invite", token] as const,
};

/* ───────────── 조회 ───────────── */

export const useBootstrap = () => useQuery({ queryKey: keys.bootstrap, queryFn: () => http.get<BootstrapDTO>("/api/bootstrap"), staleTime: Infinity });

export function useMe(): MeDTO {
  const { data } = useBootstrap();
  if (!data?.me) throw new Error("로그인 정보가 없습니다.");
  return data.me;
}

export const useChannels = () => useQuery({ queryKey: keys.channels, queryFn: () => http.get<ChannelDTO[]>("/api/channels"), staleTime: Infinity });

export const useConversations = () => useQuery({ queryKey: keys.conversations, queryFn: () => http.get<ConversationDTO[]>("/api/conversations"), staleTime: Infinity });

export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => http.get<UserDTO[]>("/api/users"), staleTime: Infinity });

export function useUserMap(): Map<number, UserDTO> {
  const { data } = useUsers();
  return new Map((data ?? []).map((u) => [u.id, u]));
}

type MessagesData = InfiniteData<MessagePage, number | undefined>;

/** pages[0]이 최신 페이지, 뒤로 갈수록 오래된 페이지 */
export function useMessages(room: RoomRef) {
  return useInfiniteQuery({
    queryKey: keys.messages(room),
    queryFn: ({ pageParam }) => http.get<MessagePage>(`${roomApi(room)}/messages${pageParam ? `?before=${pageParam}` : ""}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.messages[0]?.id : undefined),
    staleTime: Infinity,
  });
}

export const useThread = (rootId: number) =>
  useQuery({ queryKey: keys.thread(rootId), queryFn: () => http.get<ThreadDTO>(`/api/messages/${rootId}/thread`), staleTime: Infinity });

export const useThreads = () => useQuery({ queryKey: keys.threads, queryFn: () => http.get<ThreadSummaryDTO[]>("/api/threads"), staleTime: 30_000 });

export const useMoveRequests = (conversationId: number) =>
  useQuery({ queryKey: keys.moves(conversationId), queryFn: () => http.get<MoveRequestDTO[]>(`/api/conversations/${conversationId}/move-requests`), staleTime: Infinity });

export const useNotifications = () =>
  useQuery({ queryKey: keys.notifications, queryFn: () => http.get<NotificationPage>("/api/notifications"), staleTime: Infinity });

export function useMarkNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ids?: number[]; all?: boolean }) => http.post("/api/notifications/read", body),
    onMutate: (body) => markNotificationsLocal(qc, body.all ? "all" : (body.ids ?? [])),
  });
}

export function markNotificationsLocal(qc: QueryClient, ids: number[] | "all") {
  qc.setQueryData<NotificationPage>(keys.notifications, (p) => {
    if (!p) return p;
    const notifications = p.notifications.map((n) => (ids === "all" || ids.includes(n.id) ? { ...n, read: true } : n));
    const newlyRead = p.notifications.filter((n) => !n.read && (ids === "all" || ids.includes(n.id))).length;
    return { ...p, notifications, unread: ids === "all" ? 0 : Math.max(0, p.unread - newlyRead) };
  });
}

export function useSetNotifyLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, level }: { channelId: number; level: NotifyLevel }) => http.patch(`/api/channels/${channelId}/notify`, { level }),
    onMutate: ({ channelId, level }) => patchChannel(qc, channelId, { notifyLevel: level }),
  });
}

export const useInvites = () => useQuery({ queryKey: keys.invites, queryFn: () => http.get<InviteDTO[]>("/api/invites") });

export const useInviteInfo = (token: string) =>
  useQuery({ queryKey: keys.invite(token), queryFn: () => http.get<InviteInfoDTO>(`/api/invites/${token}`), retry: false });

/* ───────────── 캐시 갱신 도우미 (실시간 이벤트와 응답이 같이 쓴다) ───────────── */

/** 본문에 보이는 메시지인지 (원글 또는 "채널에도 보내기" 답글) */
export const inBody = (m: MessageDTO) => m.parentId === null || m.alsoInChannel;

/**
 * 메시지를 캐시에 반영한다: 본문, 스레드(원글 또는 답글)에 각각.
 * 새 메시지면 붙이고, 있던 메시지면 바꾼다.
 */
export function upsertMessage(qc: QueryClient, message: MessageDTO) {
  if (inBody(message)) upsertBodyMessage(qc, message);
  const rootId = message.parentId ?? message.id;
  qc.setQueryData<ThreadDTO>(keys.thread(rootId), (t) => {
    if (!t) return t;
    if (message.id === t.root.id) return { ...t, root: message };
    const exists = t.replies.some((r) => r.id === message.id);
    return { ...t, replies: exists ? t.replies.map((r) => (r.id === message.id ? message : r)) : [...t.replies, message].sort((a, b) => a.id - b.id) };
  });
}

/** 만료되어 지워진 DM 메시지를 캐시에서 뺀다 */
export function removeMessages(qc: QueryClient, room: RoomRef, ids: number[]) {
  const gone = new Set(ids);
  qc.setQueryData<MessagesData>(keys.messages(room), (data) => data && { ...data, pages: data.pages.map((p) => ({ ...p, messages: p.messages.filter((m) => !gone.has(m.id)) })) });
  for (const id of ids) qc.removeQueries({ queryKey: keys.thread(id) });
}

/** 리액션만 바꾼다 (본문과 스레드 모두) */
export function patchReactions(qc: QueryClient, messageId: number, room: RoomRef, parentId: number | null, reactions: ReactionDTO[]) {
  const patch = (m: MessageDTO) => (m.id === messageId ? { ...m, reactions } : m);
  qc.setQueryData<MessagesData>(keys.messages(room), (data) => data && { ...data, pages: data.pages.map((p) => ({ ...p, messages: p.messages.map(patch) })) });
  qc.setQueryData<ThreadDTO>(keys.thread(parentId ?? messageId), (t) => t && { ...t, root: patch(t.root), replies: t.replies.map(patch) });
}

function upsertBodyMessage(qc: QueryClient, message: MessageDTO) {
  qc.setQueryData<MessagesData>(keys.messages(roomOfMessage(message)), (data) => {
    if (!data) return data;
    let found = false;
    const pages = data.pages.map((p) => ({
      ...p,
      messages: p.messages.map((m) => {
        if (m.id !== message.id) return m;
        found = true;
        return message;
      }),
    }));
    if (!found && pages[0]) {
      const newest = pages[0];
      const messages = [...newest.messages, message].sort((a, b) => a.id - b.id);
      pages[0] = { ...newest, messages };
    }
    return { ...data, pages };
  });
}

export function patchChannel(qc: QueryClient, id: number, patch: Partial<ChannelDTO> | ((c: ChannelDTO) => Partial<ChannelDTO>)) {
  qc.setQueryData<ChannelDTO[]>(keys.channels, (list) =>
    list?.map((c) => (c.id === id ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) } : c)),
  );
}

export function patchConversation(qc: QueryClient, id: number, patch: Partial<ConversationDTO> | ((c: ConversationDTO) => Partial<ConversationDTO>)) {
  qc.setQueryData<ConversationDTO[]>(keys.conversations, (list) =>
    list?.map((c) => (c.id === id ? { ...c, ...(typeof patch === "function" ? patch(c) : patch) } : c)),
  );
}

/** 채널·대화 목록의 안 읽음·읽은 위치를 함께 다룬다 */
export function patchRoom(qc: QueryClient, room: RoomRef, patch: (r: { unread: number; lastReadMessageId: number | null }) => Partial<{ unread: number; lastReadMessageId: number | null }>) {
  if (room.kind === "channel") patchChannel(qc, room.id, patch);
  else patchConversation(qc, room.id, patch);
}

export function upsertUser(qc: QueryClient, user: UserDTO) {
  qc.setQueryData<UserDTO[]>(keys.users, (list) => {
    if (!list) return list;
    return list.some((u) => u.id === user.id) ? list.map((u) => (u.id === user.id ? user : u)) : [...list, user];
  });
  qc.setQueryData<BootstrapDTO>(keys.bootstrap, (b) => (b?.me && b.me.id === user.id ? { ...b, me: { ...b.me, ...user } } : b));
}

/* ───────────── 변경 ───────────── */

export interface SendInput {
  body: string;
  attachmentIds?: number[];
  parentId?: number;
  alsoInChannel?: boolean;
  silent?: boolean;
}

/* ───────────── 보내는 중인 메시지 ───────────── */

/** 보내는 중(서버 id가 생기기 전)인 메시지. id는 진짜 메시지보다 커서 목록 맨 뒤에 놓인다 */
export type PendingMessage = MessageDTO & { clientId: string };
const PENDING_BASE = 2 ** 50;
let pendingSeq = 0;

export const isPending = (m: MessageDTO): m is PendingMessage => m.id >= PENDING_BASE;

/** 보내기를 누른 순간 화면에 먼저 보일 메시지 */
export function pendingMessage(room: RoomRef, authorId: number, input: SendInput, attachments: AttachmentDTO[]): PendingMessage {
  const seq = ++pendingSeq;
  return {
    id: PENDING_BASE + seq,
    clientId: `${Date.now().toString(36)}-${seq}-${Math.random().toString(36).slice(2, 8)}`,
    channelId: room.kind === "channel" ? room.id : null,
    conversationId: room.kind === "dm" ? room.id : null,
    expiresAt: null,
    authorId,
    kind: "user",
    body: input.body,
    createdAt: new Date().toISOString(),
    editedAt: null,
    deleted: false,
    reactions: [],
    attachments,
    parentId: input.parentId ?? null,
    alsoInChannel: !!input.parentId && !!input.alsoInChannel,
    replyCount: 0,
    lastReplyAt: null,
    replyUserIds: [],
    task: null,
    taskIds: [],
    wiki: null,
    previews: [],
  };
}

/** 보내는 중인 메시지를 캐시에서 뺀다 (진짜 메시지가 왔거나 보내지 못했을 때) */
export function removePending(qc: QueryClient, room: RoomRef, clientId: string, parentId: number | null) {
  const keep = (m: MessageDTO) => !(isPending(m) && m.clientId === clientId);
  qc.setQueryData<MessagesData>(keys.messages(room), (data) => data && { ...data, pages: data.pages.map((p) => (p.messages.some((m) => !keep(m)) ? { ...p, messages: p.messages.filter(keep) } : p)) });
  if (parentId !== null) qc.setQueryData<ThreadDTO>(keys.thread(parentId), (t) => t && { ...t, replies: t.replies.filter(keep) });
}

/**
 * 메시지 보내기. 누르는 순간 목록에 먼저 보이고(보내는 중), 서버 응답이나 실시간 이벤트(clientId)가 오면 진짜 메시지로 바뀐다.
 * 보내지 못하면 목록에서 빼고, 부르는 쪽이 쓴 글을 되돌린다.
 */
/** 한곳에서 연달아 보낸 메시지는 보낸 순서대로 서버에 닿게 하나씩 보낸다 */
const sendQueues = new Map<string, Promise<unknown>>();

export function useSendMessage(room: RoomRef) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input, pending }: { input: SendInput; pending: PendingMessage }) => {
      const key = `${room.kind}:${room.id}`;
      const run = (sendQueues.get(key) ?? Promise.resolve())
        .catch(() => {})
        .then(() => http.post<MessageDTO>(`${roomApi(room)}/messages`, { ...input, clientId: pending.clientId }));
      sendQueues.set(key, run);
      return run;
    },
    onMutate: ({ pending }) => upsertMessage(qc, pending),
    onSuccess: (m, { pending }) => {
      removePending(qc, room, pending.clientId, pending.parentId);
      upsertMessage(qc, m);
      if (inBody(m)) patchRoom(qc, room, (r) => ({ lastReadMessageId: Math.max(r.lastReadMessageId ?? 0, m.id) }));
      if (room.kind === "dm") patchConversation(qc, room.id, { lastMessageAt: m.createdAt });
    },
    onError: (_err, { pending }) => removePending(qc, room, pending.clientId, pending.parentId),
  });
}

/** 리액션 누르기/취소 (응답이 오기 전에 화면부터 바꾼다) */
export function useToggleReaction(meId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ message, emoji, on }: { message: MessageDTO; emoji: string; on: boolean }) =>
      (on ? http.put : http.delete)<ReactionDTO[]>(`/api/messages/${message.id}/reactions/${encodeURIComponent(emoji)}`),
    onMutate: ({ message, emoji, on }) => {
      const list = message.reactions.map((r) => ({ ...r, userIds: r.userIds.filter((id) => id !== meId || r.emoji !== emoji) }));
      const target = list.find((r) => r.emoji === emoji);
      if (on) {
        if (target) target.userIds.push(meId);
        else list.push({ emoji, userIds: [meId] });
      }
      patchReactions(qc, message.id, roomOfMessage(message), message.parentId, list.filter((r) => r.userIds.length > 0));
    },
    onSuccess: (reactions, { message }) => patchReactions(qc, message.id, roomOfMessage(message), message.parentId, reactions),
  });
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rootId, messageId }: { rootId: number; messageId: number }) => http.post(`/api/messages/${rootId}/thread/read`, { messageId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.threads }),
  });
}

export function useFollowThread(rootId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { following: boolean; muted?: boolean }) => http.put<{ following: boolean; muted: boolean }>(`/api/messages/${rootId}/follow`, body),
    onSuccess: (r) => {
      qc.setQueryData<ThreadDTO>(keys.thread(rootId), (t) => t && { ...t, ...r });
      void qc.invalidateQueries({ queryKey: keys.threads });
    },
  });
}

export function useEditMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => http.patch<MessageDTO>(`/api/messages/${id}`, { body }),
    onSuccess: (m) => upsertMessage(qc, m),
  });
}

export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete<MessageDTO>(`/api/messages/${id}`),
    onSuccess: (m) => upsertMessage(qc, m),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ room, messageId }: { room: RoomRef; messageId: number }) => http.post(`${roomApi(room)}/read`, { messageId }),
    onMutate: ({ room, messageId }) => patchRoom(qc, room, (r) => ({ unread: 0, lastReadMessageId: Math.max(r.lastReadMessageId ?? 0, messageId) })),
  });
}

export function useChannelAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, body }: { id: number; action: "join" | "leave" | "archive"; body?: unknown }) =>
      http.post(`/api/channels/${id}/${action}`, body ?? {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels }),
  });
}

export function useStartConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: number[]) => http.post<ConversationDTO>("/api/conversations", { userIds }),
    onSuccess: (c) =>
      qc.setQueryData<ConversationDTO[]>(keys.conversations, (list) => (list ? (list.some((x) => x.id === c.id) ? list : [c, ...list]) : list)),
  });
}

export function useRequestMove(conversationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { fromMessageId: number; toMessageId: number; channelId: number }) => http.post<MoveRequestDTO>(`/api/conversations/${conversationId}/move-requests`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.moves(conversationId) }),
  });
}

export function useDecideMove(conversationId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) => http.post<MoveRequestDTO>(`/api/move-requests/${id}/decide`, { approve }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.moves(conversationId) }),
  });
}
